import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import InputItem from '../../components/Forms/InputItem/InputItem';
import Fader from '../../components/Loaders/Fader';
import AppNotify from '../../components/AppNotify/AppNotify';
import VerifyModal from '../../components/VerifyModal/VerifyModal';

import {
  login,
  sendEmailVerificationCode,
  validateEmailVerificationCode,
  sendSmsVerificationCode,
  validateSmsVerificationCode,
} from '../../../http/authAPI';
import { setSession, setSessionId } from '../../../datastore/actions/sessionActions';
import { setLoggingIn, setNotify } from '../../../datastore/actions/actionActions';
import { saveCustomState } from '../../../util/LocalStorage';
import { SESSION_STATE, USER_DATA, SESSION_ID } from '../../../util/constants/AppConstants';
import { maskEmail, maskPhone } from '../../../util/HelperFunctions';

import Logo from '../../../assets/images/logo-white.png';
import SecurityIcon from '../../../assets/images/icons/security.svg';
import LightningIcon from '../../../assets/images/icons/lightning.svg';
import TeamIcon from '../../../assets/images/icons/team.svg';
import MobileIcon from '../../../assets/images/icons/mobile.svg';
import ArrowRight from '../../../assets/images/icons/arrow-right.svg';
import './Login.scss';

const Login = () => {
  const dispatch = useDispatch();

  const config = useSelector((state) => state.config.config);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginChallenge, setLoginChallenge] = useState(false);
  const [isVerificationOpen, setIsVerificationOpen] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const [userData, setUserData] = useState([]);

  const handleUsername = (e) => setUsername(e.currentTarget.value);
  const handlePassword = (e) => setPassword(e.currentTarget.value);

  const completeLogin = (userData) => {
    saveCustomState(
      USER_DATA,
      {
        id: userData.user.id,
        firstname: userData.user.firstname,
        lastname: userData.user.lastname,
        email: userData.user.email,
        phone: userData.user.phone,
        role: userData.user.role,
        job: userData.user.job_title,
        riding: userData.user.riding,
        city: userData.user.city,
        auth_method: userData.user['2fa'],
      },
      true,
    );

    dispatch(setSessionId(userData.session.id));
    dispatch(setSession(userData.session.token));

    saveCustomState(SESSION_STATE, userData.session.token);
    saveCustomState(SESSION_ID, userData.session.id);

    setTimeout(() => {
      setIsVerificationOpen(false);
      dispatch(setLoggingIn(false));
      setLoginChallenge(false);
    }, 500);
  };

  const handleLogin = async () => {
    if (!username || !password) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Password and/or Email Missing',
        }),
      );
      return;
    }

    setLoginChallenge(true);
    dispatch(setLoggingIn(true));

    const res = await login(config, username, password);

    if (res.status === 'success') {
      setUserData(res.data);

      if (config.demo || config.bypass_2fa) {
        completeLogin(res.data);
      } else {
        const defaultMethod = res.data.user['2fa'] || 'email';

        let res2;
        if (defaultMethod === 'sms') {
          res2 = await sendSmsVerificationCode(config, `+${res.data.user.phone}`);
        } else {
          res2 = await sendEmailVerificationCode(config, res.data.user.id);
        }

        if (res2.status !== 200 || !res2.data.success) {
          dispatch(
            setNotify({
              status: true,
              type: 'error',
              title: 'Error',
              message: 'Verification code could not be sent. Please try again later',
            }),
          );
        }

        setIsVerificationOpen(true);
      }
    } else {
      if (res.message === 'Incorrect Password.' || res.message === 'No User Found.') {
        setPassword('');
      }

      setLoginChallenge(false);
      dispatch(setLoggingIn(false));
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.message,
        }),
      );
    }
  };

  const handleVerificationResend = async (method) => {
    let res;
    if (method === 'sms') {
      res = await sendSmsVerificationCode(config, `+${userData.user.phone}`);
    } else {
      res = await sendEmailVerificationCode(config, userData.user.id);
    }

    if (res.status === 200 && res.data.success) {
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: `A new ${method.toUpperCase()} verification code has been sent.`,
        }),
      );
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Verification code could not be sent. Please try again later',
        }),
      );
    }
  };

  const handleValidateVerification = async (code, method) => {
    const userCode = Number(String(code).replace(/,/g, ''));

    let res;
    if (method === 'sms') {
      res = await validateSmsVerificationCode(config, `+${userData.user.phone}`, userCode);
    } else {
      res = await validateEmailVerificationCode(config, userData.user.id, userCode);
    }

    if (res.status === 200) {
      if (!res.data.success) {
        dispatch(
          setNotify({
            status: true,
            type: 'error',
            title: 'Error',
            message: res.data.message,
          }),
        );
        setVerificationLoading(false);
        return;
      }

      completeLogin(userData);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Verification code could not be verified. Please try again later',
        }),
      );
      setVerificationLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <>
      <AppNotify />
      <div className="sign-in">
        <div className="sign-in__left">
          <div className="sign-in__logo">
            <img src={Logo} />
          </div>
          <h1 className="sign-in__title">The intelligence system for your riding</h1>
          <h1 className="sign-in__title mobile">Welcome to KYRP</h1>
          <p className="sign-in__text">Your Riding. Your Signals. Your Command Centre</p>
          <p className="sign-in__text">Convert daily messages into actionable riding intelligence</p>
          <p className="sign-in__text mobile">The intelligence system for your riding</p>
          <ul className="sign-in__list">
            <li className="sign-in__item">
              <img src={SecurityIcon} />
              Enterprise-grade security
            </li>
            <li className="sign-in__item">
              <img src={LightningIcon} />
              Lightning-fast performance
            </li>
            <li className="sign-in__item">
              <img src={TeamIcon} />
              Seamless team collaboration
            </li>
          </ul>
          <ul className="sign-in__list mobile">
            <li className="sign-in__item">
              <div className="sign-in__icon">
                <img src={SecurityIcon} />
              </div>
              Secure
            </li>
            <li className="sign-in__item">
              <div className="sign-in__icon">
                <img src={LightningIcon} />
              </div>
              Fast
            </li>
            <li className="sign-in__item">
              <div className="sign-in__icon">
                <img src={MobileIcon} />
              </div>
              Mobile
            </li>
          </ul>
          <div className={`form mobile ${loginChallenge ? 'logging-in' : ''}`}>
            <h2 className="form__title">Sign in</h2>
            <div className="form__wrap">
              <p className="form__text">Enter your credentials to continue</p>
              <InputItem
                label="Work login"
                type={'text'}
                value={username}
                onChange={handleUsername}
                onKeyDown={handleKeyDown}
              />
              <InputItem
                label="Password"
                type={'password'}
                value={password}
                onChange={handlePassword}
                onKeyDown={handleKeyDown}
              />
              <button className="form__btn" onClick={handleLogin}>
                Sign In
                <img src={ArrowRight} />
              </button>
            </div>
            {loginChallenge && (
              <div className="wrap">
                <Fader size={50} />
              </div>
            )}
          </div>
          <div className="response">
            <div className="response__text">
              "Before KYRP, we were buried in emails. Now we know exactly what matters in our riding&nbsp;— every single
              week."
              <span>— Constituency office manager</span>
            </div>
            {/* <div className="response__author">
              <div className="response__img"></div>
              <div className="response__author-data">
                <span>Sarah Johnson</span>
                <span>MPP, Ottawa</span>
              </div>
            </div> */}
          </div>
        </div>
        <div className="sign-in__right">
          <div className={`form ${loginChallenge ? 'logging-in' : ''}`}>
            <h2 className="form__title">Welcome back</h2>
            <div className="form__wrap">
              <p className="form__text">Sign in to your account to continue</p>
              <InputItem
                label="Work login"
                type={'text'}
                value={username}
                onChange={handleUsername}
                onKeyDown={handleKeyDown}
              />
              <InputItem
                label="Password"
                type={'password'}
                value={password}
                onChange={handlePassword}
                onKeyDown={handleKeyDown}
              />
              <button className="form__btn" onClick={handleLogin}>
                Sign In
                <img src={ArrowRight} />
              </button>
            </div>
            {loginChallenge && (
              <div className="wrap">
                <Fader size={50} />
              </div>
            )}
          </div>
        </div>
      </div>
      {isVerificationOpen && (
        <VerifyModal
          isOpen={isVerificationOpen}
          setIsOpen={setIsVerificationOpen}
          validation={handleValidateVerification}
          resend={handleVerificationResend}
          setLoginChallenge={setLoginChallenge}
          maskedEmail={maskEmail(userData.user.email)}
          maskedPhone={maskPhone(userData.user.phone)}
          default2faMethod={userData.user['2fa']}
          loading={verificationLoading}
          setLoading={setVerificationLoading}
        />
      )}
    </>
  );
};

export default Login;
